import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Select from 'react-select';
import Icon from '../AppIcon';
import Button from './Button';
import NotificationsPanel from './NotificationsPanel';
import useTheme from '../../hooks/useTheme';
import useLanguage from '../../hooks/useLanguage';
import { useToast } from './Toast';


const Header = ({ className = '' }) => {
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showMobileAccountSelector, setShowMobileAccountSelector] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [selectedMobileField, setSelectedMobileField] = useState('profit');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mobileMenuTab, setMobileMenuTab] = useState('account');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [createAccountData, setCreateAccountData] = useState({
    accountType: '',
    accountSubType: '',
    leverage: '1:100'
  });
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage, t, availableLanguages, isRTL } = useLanguage();
  const toast = useToast();

  // Account data
  const [accountData, setAccountData] = useState({
    profit: '0.00',
    assets: '1,000,000.00', 
    credit: '1,000,000.00',
    balance: '0.00',
    freeMargin: '1,000,000.00',
    margin: '0.00',
    marginLevel: '0.00'
  });

  // Load logo and favicon from localStorage
  useEffect(() => {
    const storedLogoUrl = localStorage.getItem('app_logo_url');
    const storedFaviconUrl = localStorage.getItem('app_favicon_url');
    
    if (storedLogoUrl) {
      setLogoUrl(storedLogoUrl);
    }
    
    if (storedFaviconUrl) {
      setFaviconUrl(storedFaviconUrl);
      // Update favicon in document head
      const faviconLink = document.querySelector('link[rel="icon"]') || document.createElement('link');
      faviconLink.type = 'image/x-icon';
      faviconLink.rel = 'icon';
      faviconLink.href = storedFaviconUrl;
      document.head?.appendChild(faviconLink);
    }
  }, []);

  // Load accounts on component mount
  useEffect(() => {
    loadAccounts();
    loadNotifications();
  }, []);

  // Load available accounts
  const loadAccounts = async () => {
    try {
      // In a real implementation, this would fetch from the API
      const mockAccounts = [
        { id: 1, name: 'Demo Account', type: 'demo', balance: 10000, currency: 'USD', leverage: '1:100' },
        { id: 2, name: 'Live Account #1', type: 'live', balance: 5000, currency: 'USD', leverage: '1:50' },
        { id: 3, name: 'Live Account #2', type: 'live', balance: 15000, currency: 'EUR', leverage: '1:30' }
      ];
      
      setAvailableAccounts(mockAccounts);
      
      // Set first account as selected by default
      if (mockAccounts?.length > 0) {
        setSelectedAccount(mockAccounts?.[0]);
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
      // Set fallback account
      const fallbackAccount = { id: 1, name: 'Demo Account', type: 'demo', balance: 10000, currency: 'USD', leverage: '1:100' };
      setAvailableAccounts([fallbackAccount]);
      setSelectedAccount(fallbackAccount);
    }
  };

  // Load notifications
  const loadNotifications = async () => {
    try {
      // Mock notifications - in real app, fetch from API
      const mockNotifications = [
        {
          id: 1,
          type: 'trade',
          title: 'Position Closed',
          message: 'EURUSD position closed with +$125.50 profit',
          timestamp: new Date(Date.now() - 5 * 60 * 1000),
          read: false,
          icon: 'TrendingUp'
        },
        {
          id: 2,
          type: 'alert',
          title: 'Price Alert',
          message: 'GBPUSD reached target price 1.3500',
          timestamp: new Date(Date.now() - 15 * 60 * 1000),
          read: false,
          icon: 'Bell'
        },
        {
          id: 3,
          type: 'news',
          title: 'Market News',
          message: 'Fed announces interest rate decision',
          timestamp: new Date(Date.now() - 30 * 60 * 1000),
          read: true,
          icon: 'Newspaper'
        }
      ];
      
      setNotifications(mockNotifications);
      setUnreadCount(mockNotifications?.filter(n => !n?.read)?.length);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const handleAccountSwitch = (account) => {
    setSelectedAccount(account);
    setShowAccountDropdown(false);
    
    // Update account data based on selected account
    // In real implementation, fetch actual account data from API
  };

  const handleCreateAccountSubmit = (e) => {
    e?.preventDefault();
    
    // Validate account type selection
    if (!createAccountData?.accountType) {
      toast.error(t('pleaseSelectAccountType'), 3000);
      return;
    }
    
    // Validate real account sub type
    if (createAccountData?.accountType === 'real' && !createAccountData?.accountSubType) {
      toast.error(t('pleaseSelectAccountFeatures'), 3000);
      return;
    }
    
    // Create new account
    const newAccount = {
      id: Date.now(),
      name: `${createAccountData?.accountType === 'demo' ? t('demoAccount') : t('realAccount')} #${availableAccounts.length + 1}`,
      type: createAccountData?.accountType,
      subType: createAccountData?.accountSubType,
      balance: createAccountData?.accountType === 'demo' ? 10000 : 0,
      currency: 'USD',
      leverage: createAccountData?.leverage,
      createdAt: new Date()
    };
    
    // Add to accounts list
    setAvailableAccounts(prev => [...prev, newAccount]);
    
    // Set as selected account
    setSelectedAccount(newAccount);
    
    // Show success message
    toast.success(`${newAccount.name} ${t('accountCreatedSuccessfully')}`, 5000);
    
    // Close modal and reset form
    setShowCreateAccountModal(false);
    setCreateAccountData({
      accountType: '',
      accountSubType: '',
      leverage: '1:100'
    });
    setShowAccountDropdown(false);
  };

  const markNotificationRead = (notificationId) => {
    setNotifications(prev =>
      prev?.map(notification =>
        notification?.id === notificationId
          ? { ...notification, read: true }
          : notification
      )
    );
    
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllNotificationsRead = () => {
    setNotifications(prev =>
      prev?.map(notification => ({ ...notification, read: true }))
    );
    setUnreadCount(0);
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  // Mobile account fields for selectbox
  const mobileAccountFields = [
    { key: 'profit', label: t('profit'), value: accountData?.profit },
    { key: 'assets', label: t('assets'), value: accountData?.assets?.replace('1,000,000.00', '1M') },
    { key: 'credit', label: t('credit'), value: accountData?.credit?.replace('1,000,000.00', '1M') },
    { key: 'balance', label: t('balance'), value: accountData?.balance },
    { key: 'freeMargin', label: t('freeMargin'), value: accountData?.freeMargin?.replace('1,000,000.00', '1M') }
  ];

  const selectedFieldData = mobileAccountFields?.find(field => field?.key === selectedMobileField);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(currentLanguage === 'tr' ? 'tr-TR' : currentLanguage === 'ar' ? 'ar-SA' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(parseFloat(amount?.replace(/,/g, '')));
  };

  return (
    <>
      <header className={`trading-header h-16 ${className} ${isRTL ? 'rtl' : ''}`}>
        <div className="flex items-center justify-between h-full px-4">
          {/* Left: Logo and Account Selector */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-4">
              {/* Logo */}
              <div className="flex items-center space-x-2">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="Logo" 
                    className="w-8 h-8 rounded-lg object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-8 h-8 bg-primary rounded-lg flex items-center justify-center ${logoUrl ? 'hidden' : ''}`}>
                  <Icon name="TrendingUp" size={20} color="white" />
                </div>
                <span className="hidden sm:inline text-xl font-semibold text-foreground">Zonex Trader</span>
                <span className="sm:hidden text-base font-semibold text-foreground">Zonex</span>
              </div>
              
              {/* Account Selector - Desktop Only */}
              <div className="hidden md:block relative">
                <button
                  onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md hover:bg-muted transition-colors border border-border min-w-[140px]"
                >
                  <div className="flex flex-col items-start min-w-0 flex-1">
                    <span className="text-xs text-muted-foreground">{t('account')}</span>
                    <div className="flex items-center space-x-1">
                      <div className={`w-2 h-2 rounded-full ${selectedAccount?.type === 'live' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                      <span className="font-medium text-foreground text-xs truncate">
                        {selectedAccount?.name || 'Demo Account'}
                      </span>
                    </div>
                  </div>
                  <Icon name="ChevronDown" size={14} />
                </button>

                {showAccountDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-popover border border-border rounded-md shadow-lg z-dropdown animate-fade-in">
                    <div className="p-1 max-h-64 overflow-y-auto">
                      {availableAccounts?.map((account) => (
                        <button
                          key={account?.id}
                          onClick={() => handleAccountSwitch(account)}
                          className={`w-full text-left px-3 py-3 text-sm rounded-md hover:bg-muted transition-colors ${
                            selectedAccount?.id === account?.id ? 'bg-muted text-foreground' : 'text-muted-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className={`w-2 h-2 rounded-full ${account?.type === 'live' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                              <div className="flex flex-col">
                                <span className="font-medium">{account?.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {account?.balance?.toLocaleString()} {account?.currency} • {account?.leverage}
                                </span>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              account?.type === 'live' ?'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                            }`}>
                              {account?.type?.toUpperCase()}
                            </span>
                          </div>
                        </button>
                      ))}
                      
                      {/* Divider */}
                      <div className="my-2">
                        <hr className="border-border" />
                      </div>
                      
                      {/* Create Account Button */}
                      <button
                        onClick={() => {
                          setShowCreateAccountModal(true);
                          setShowAccountDropdown(false);
                        }}
                        className="w-full text-left px-3 py-3 text-sm rounded-md hover:bg-muted transition-colors text-primary hover:text-primary/80"
                      >
                        <div className="flex items-center space-x-2">
                          <Icon name="Plus" size={16} />
                          <span className="font-medium">{t('createAccount')}</span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Navigation Menu */}
            <nav className="hidden md:flex items-center space-x-2">
              <Link 
                to="/transaction"
                className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location?.pathname === '/transaction' || location?.pathname === '/' || location?.pathname === '/main-trading-dashboard' ?'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="BarChart3" size={16} />
                <span>{t('transaction')}</span>
              </Link>
              <Link
                to="/prop" 
                className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location?.pathname === '/prop' || location?.pathname === '/market-analysis-hub' ?'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Target" size={16} />
                <span>{t('prop')}</span>
              </Link>
              <Link
                to="/explore"
                className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location?.pathname === '/explore' || location?.pathname === '/portfolio-analytics' ?'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Check" size={16} />
                <span>{t('explore')}</span>
              </Link>
            </nav>
          </div>

          {/* Center: Account Information - Updated with color-coded styling */}
          <div className="hidden xl:flex items-center space-x-6">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">{t('profit')}</span>
                  <span className="font-mono font-medium text-foreground text-sm">0.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">{t('assets')}</span>
                  <span className="font-mono font-medium text-emerald-500 text-sm">1 000 000.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">{t('credit')}</span>
                  <span className="font-mono font-medium text-emerald-500 text-sm">1 000 000.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">{t('balance')}</span>
                  <span className="font-mono font-medium text-foreground text-sm">0.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">{t('freeMargin')}</span>
                  <span className="font-mono font-medium text-emerald-500 text-sm">1 000 000.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">{t('margin')}</span>
                  <span className="font-mono font-medium text-foreground text-sm">0.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">{t('marginLevel')}</span>
                  <span className="font-mono font-medium text-foreground text-sm">0.00 %</span>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Account Summary - SelectBox for Mobile and Tablet */}
          <div className="xl:hidden relative flex-shrink-0">
            <button
              onClick={() => setShowMobileAccountSelector(!showMobileAccountSelector)}
              className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-sm font-medium rounded-md hover:bg-muted transition-colors border border-border min-w-[100px] sm:min-w-[120px]"
            >
              <div className="flex flex-col items-start min-w-0">
                <span className="text-xs text-muted-foreground truncate">{selectedFieldData?.label}</span>
                <span className="font-mono font-medium text-foreground text-xs truncate">{selectedFieldData?.value}</span>
              </div>
              <Icon name="ChevronDown" size={12} className="sm:hidden" />
              <Icon name="ChevronDown" size={14} className="hidden sm:inline" />
            </button>

            {showMobileAccountSelector && (
              <div className="absolute top-full left-0 mt-1 w-40 sm:w-48 bg-popover border border-border rounded-md shadow-lg z-dropdown animate-fade-in">
                <div className="p-1 max-h-64 overflow-y-auto">
                  {mobileAccountFields?.map((field) => (
                    <button
                      key={field?.key}
                      onClick={() => {
                        setSelectedMobileField(field?.key);
                        setShowMobileAccountSelector(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors flex flex-col items-start space-y-1 ${
                        selectedMobileField === field?.key ? 'bg-muted text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      <span className="text-xs text-muted-foreground">{field?.label}</span>
                      <span className="font-mono font-medium">{field?.value}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Controls */}
          <div className="flex items-center space-x-1 flex-shrink-0">
            {/* Desktop Controls */}
            <div className="hidden md:flex items-center space-x-1">
              {/* Language Selector - Desktop */}
              <div className="relative">
                <button
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                  className="flex items-center space-x-1 px-2 py-2 text-sm font-medium rounded-md hover:bg-muted transition-colors"
                >
                  <span className="text-base">{availableLanguages?.find(lang => lang?.code === currentLanguage)?.flag}</span>
                  <span className="text-sm">{availableLanguages?.find(lang => lang?.code === currentLanguage)?.name}</span>
                  <Icon name="ChevronDown" size={14} />
                </button>

                {showLanguageDropdown && (
                  <div className="absolute top-full right-0 mt-1 w-40 bg-popover border border-border rounded-md shadow-lg z-dropdown animate-fade-in">
                    <div className="p-1">
                      {availableLanguages?.map((language) => (
                        <button
                          key={language?.code}
                          onClick={() => {
                            changeLanguage(language?.code);
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors flex items-center space-x-2 ${
                            currentLanguage === language?.code ? 'bg-muted text-foreground' : 'text-muted-foreground'
                          }`}
                        >
                          <span>{language?.flag}</span>
                          <span>{language?.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Theme Toggle - Desktop */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="w-8 h-8 p-0 flex-shrink-0"
              >
                <Icon name={isDarkMode ? "Sun" : "Moon"} size={16} />
              </Button>

              {/* Notifications - Desktop */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="w-8 h-8 p-0 relative flex-shrink-0"
                >
                  <Icon name="Bell" size={16} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center leading-none">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden w-8 h-8 p-0 flex-shrink-0 relative"
            >
              <Icon name={showMobileMenu ? "X" : "Menu"} size={20} />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      {showMobileMenu && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/60 animate-fade-in" onClick={() => setShowMobileMenu(false)}>
          <div 
            className="absolute top-16 right-0 w-80 max-w-[85vw] h-[calc(100vh-4rem)] bg-card border-l border-border shadow-2xl flex flex-col animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Tabs Header */}
            <div className="flex border-b border-border bg-muted/20">
              <button
                onClick={() => setMobileMenuTab('account')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  mobileMenuTab === 'account'
                    ? 'text-primary border-b-2 border-primary bg-background'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="User" size={16} />
                <span>{t('account')}</span>
              </button>
              
              <button
                onClick={() => setMobileMenuTab('settings')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  mobileMenuTab === 'settings'
                    ? 'text-primary border-b-2 border-primary bg-background'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Settings" size={16} />
                <span>{t('settings')}</span>
              </button>
              
              <button
                onClick={() => setMobileMenuTab('notifications')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 relative ${
                  mobileMenuTab === 'notifications'
                    ? 'text-primary border-b-2 border-primary bg-background'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Bell" size={16} />
                <span>{t('notifications')}</span>
                {unreadCount > 0 && (
                  <span className="absolute top-2 right-2 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center leading-none">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Account Tab Content */}
              {mobileMenuTab === 'account' && (
                <>
                  {/* Selected Account Summary - Mobile */}
                  <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-4 border border-primary/20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${selectedAccount?.type === 'live' ? 'bg-green-500' : 'bg-blue-500'} animate-pulse`}></div>
                    <h3 className="font-bold text-foreground">{selectedAccount?.name || 'Demo Account'}</h3>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${
                    selectedAccount?.type === 'live' 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  }`}>
                    {selectedAccount?.type?.toUpperCase()}
                  </span>
                </div>
                
                {/* Account Details Grid */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-background/50 rounded-lg p-2">
                    <div className="text-[9px] text-muted-foreground uppercase">{t('balance')}</div>
                    <div className="font-mono font-bold text-sm text-foreground">{accountData?.balance} USD</div>
                  </div>
                  <div className="bg-background/50 rounded-lg p-2">
                    <div className="text-[9px] text-muted-foreground uppercase">{t('profit')}</div>
                    <div className="font-mono font-bold text-sm text-emerald-500">{accountData?.profit} USD</div>
                  </div>
                  <div className="bg-background/50 rounded-lg p-2">
                    <div className="text-[9px] text-muted-foreground uppercase">{t('equity')}</div>
                    <div className="font-mono font-semibold text-xs text-foreground">{accountData?.assets?.replace('1,000,000.00', '1M')} USD</div>
                  </div>
                  <div className="bg-background/50 rounded-lg p-2">
                    <div className="text-[9px] text-muted-foreground uppercase">{t('margin')}</div>
                    <div className="font-mono font-semibold text-xs text-foreground">{accountData?.margin} USD</div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-1 text-muted-foreground">
                    <Icon name="TrendingUp" size={12} />
                    <span>{t('leverage')}: {selectedAccount?.leverage || '1:100'}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-emerald-500">
                    <Icon name="Shield" size={12} />
                    <span>{t('freeMargin')}: {accountData?.freeMargin?.replace('1,000,000.00', '1M')}</span>
                  </div>
                </div>
              </div>

              {/* Account Selector - Mobile */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('account')}</h3>
                <div className="space-y-2">
                  {availableAccounts?.map((account) => (
                    <button
                      key={account?.id}
                      onClick={() => {
                        handleAccountSwitch(account);
                        setShowMobileMenu(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                        selectedAccount?.id === account?.id 
                          ? 'bg-primary/10 border-primary text-foreground' 
                          : 'bg-muted/30 border-border text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${account?.type === 'live' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                          <div className="flex flex-col">
                            <span className="font-medium text-sm">{account?.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {account?.balance?.toLocaleString()} {account?.currency}
                            </span>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          account?.type === 'live' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                        }`}>
                          {account?.type?.toUpperCase()}
                        </span>
                      </div>
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setShowCreateAccountModal(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg border border-dashed border-primary text-primary hover:bg-primary/5 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="Plus" size={16} />
                      <span className="font-medium text-sm">{t('createAccount')}</span>
                    </div>
                  </button>
                </div>
              </div>
                </>
              )}

              {/* Settings Tab Content */}
              {mobileMenuTab === 'settings' && (
                <>
                  {/* Language Selector - Mobile */}
                  <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('language')}</h3>
                <div className="space-y-2">
                  {availableLanguages?.map((language) => (
                    <button
                      key={language?.code}
                      onClick={() => {
                        changeLanguage(language?.code);
                        setShowMobileMenu(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors flex items-center space-x-3 ${
                        currentLanguage === language?.code 
                          ? 'bg-primary/10 border-primary text-foreground' 
                          : 'bg-muted/30 border-border text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      <span className="text-2xl">{language?.flag}</span>
                      <span className="font-medium">{language?.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Theme Toggle - Mobile */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('appearance')}</h3>
                <button
                  onClick={() => {
                    toggleTheme();
                    setShowMobileMenu(false);
                  }}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-muted/30 hover:bg-muted transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <Icon name={isDarkMode ? "Moon" : "Sun"} size={20} />
                    <span className="font-medium">{isDarkMode ? t('darkMode') : t('lightMode')}</span>
                  </div>
                  <div className={`w-11 h-6 rounded-full transition-colors ${isDarkMode ? 'bg-primary' : 'bg-gray-300'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white mt-0.5 transition-transform ${isDarkMode ? 'translate-x-5.5' : 'translate-x-0.5'}`}></div>
                  </div>
                </button>
              </div>
                </>
              )}

              {/* Notifications Tab Content */}
              {mobileMenuTab === 'notifications' && (
                <>
                  {/* Notifications - Mobile */}
                  <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('notifications')}</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllNotificationsRead}
                      className="text-xs text-primary hover:underline"
                    >
                      {t('markAllRead')}
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {notifications?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="Bell" size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t('noNotifications')}</p>
                    </div>
                  ) : (
                    notifications?.map((notification) => (
                      <div
                        key={notification?.id}
                        onClick={() => markNotificationRead(notification?.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          notification?.read 
                            ? 'bg-muted/20 border-border' 
                            : 'bg-primary/5 border-primary/20'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <Icon name={notification?.icon} size={16} className="mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{notification?.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{notification?.message}</p>
                            <p className="text-xs text-muted-foreground mt-2">{formatTimeAgo(notification?.timestamp)}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Create Account Modal - Enhanced Design */}
      {showCreateAccountModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg border border-border overflow-visible">
            {/* Header with Gradient */}
            <div className="bg-gradient-to-r from-[#23827D] to-[#1a6862] p-6 relative overflow-hidden rounded-t-2xl">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Icon name="UserPlus" size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{t('newAccount')}</h3>
                    <p className="text-xs text-white/80">{t('createNewTradingAccount')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateAccountModal(false)}
                  className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
                >
                  <Icon name="X" size={18} className="text-white" />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateAccountSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Account Type - Card Selection */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-3">
                  {t('accountType')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { 
                      value: 'demo', 
                      icon: 'Layers', 
                      label: t('demoAccount'), 
                      desc: t('practiceWithVirtualMoney'), 
                      color: 'blue',
                      minDeposit: 0,
                      maxLeverage: '1:500',
                      availableLeverages: ['1:50', '1:100', '1:200', '1:500']
                    },
                    { 
                      value: 'real', 
                      icon: 'Zap', 
                      label: t('realAccount'), 
                      desc: t('tradeWithRealMoney'), 
                      color: 'green',
                      minDeposit: 100,
                      maxLeverage: '1:400',
                      availableLeverages: ['1:30', '1:50', '1:100', '1:200', '1:400']
                    }
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setCreateAccountData({
                        ...createAccountData, 
                        accountType: type.value, 
                        accountSubType: '',
                        leverage: type.value === 'demo' ? '1:100' : '1:100',
                        accountTypeData: type
                      })}
                      className={`p-5 rounded-xl border-2 transition-all hover:scale-105 ${
                        createAccountData?.accountType === type.value
                          ? type.value === 'demo'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg shadow-blue-500/20'
                            : 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-lg shadow-green-500/20'
                          : 'border-border bg-muted/30 hover:border-muted-foreground'
                      }`}
                    >
                      <Icon 
                        name={type.icon} 
                        size={32} 
                        className={`mx-auto mb-3 ${
                          createAccountData?.accountType === type.value 
                            ? type.value === 'demo' ? 'text-blue-600' : 'text-green-600'
                            : 'text-muted-foreground'
                        }`} 
                      />
                      <div className="text-base font-bold text-foreground mb-1">{type.label}</div>
                      <div className="text-xs text-muted-foreground mb-2">{type.desc}</div>
                      
                      {/* Account Info */}
                      <div className="flex items-center justify-between text-xs pt-2 border-t border-border/30">
                        <div className="flex items-center gap-1">
                          <Icon name="DollarSign" size={12} className="text-muted-foreground" />
                          <span className="text-muted-foreground">
                            {type.minDeposit === 0 ? t('free') : `$${type.minDeposit}+`}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Gauge" size={12} className="text-muted-foreground" />
                          <span className="text-muted-foreground">{type.maxLeverage}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Real Account Sub Options */}
              {createAccountData?.accountType === 'real' && (
                <div className="space-y-3 animate-fade-in">
                  <label className="block text-sm font-semibold text-foreground">
                    {t('accountFeatures')}
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { value: 'standard', icon: 'TrendingUp', label: t('standardAccount'), desc: t('standardFeatures') },
                      { value: 'islamic', icon: 'Star', label: t('islamicAccount'), desc: t('swapFree') },
                      { value: 'vip', icon: 'Crown', label: t('vipAccount'), desc: t('premiumSupport') },
                      { value: 'no_commission', icon: 'DollarSign', label: t('noCommissionAccount'), desc: t('zeroCommission') }
                    ].map((subType) => (
                      <button
                        key={subType.value}
                        type="button"
                        onClick={() => setCreateAccountData({...createAccountData, accountSubType: subType.value})}
                        className={`p-3 rounded-lg border transition-all text-left ${
                          createAccountData?.accountSubType === subType.value
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : 'border-border bg-muted/20 hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon 
                            name={subType.icon} 
                            size={20} 
                            className={createAccountData?.accountSubType === subType.value ? 'text-primary' : 'text-muted-foreground'} 
                          />
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-foreground">{subType.label}</div>
                            <div className="text-xs text-muted-foreground">{subType.desc}</div>
                          </div>
                          {createAccountData?.accountSubType === subType.value && (
                            <Icon name="CheckCircle2" size={18} className="text-primary" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Leverage Selection - React Select */}
              {createAccountData?.accountType && (
                <div className="animate-fade-in">
                  <label className="block text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                    <Icon name="Gauge" size={16} className="text-primary" />
                    {t('selectLeverage')}
                  </label>
                  <Select
                    value={{ 
                      value: createAccountData?.leverage, 
                      label: createAccountData?.leverage 
                    }}
                    onChange={(option) => setCreateAccountData({...createAccountData, leverage: option.value})}
                    options={createAccountData?.accountTypeData?.availableLeverages?.map(lev => ({
                      value: lev,
                      label: lev
                    }))}
                    isSearchable={false}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        backgroundColor: isDarkMode ? '#1E293B' : '#FFFFFF',
                        borderColor: state.isFocused ? '#23827D' : (isDarkMode ? '#334155' : '#E2E8F0'),
                        borderWidth: '2px',
                        borderRadius: '0.5rem',
                        padding: '0.25rem',
                        boxShadow: state.isFocused ? '0 0 0 2px rgba(35, 130, 125, 0.2)' : 'none',
                        '&:hover': {
                          borderColor: '#23827D'
                        },
                        minHeight: '44px'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected 
                          ? '#23827D' 
                          : state.isFocused 
                            ? (isDarkMode ? '#334155' : '#F1F5F9')
                            : 'transparent',
                        color: state.isSelected ? '#FFFFFF' : (isDarkMode ? '#F8FAFC' : '#0F172A'),
                        fontFamily: 'JetBrains Mono, monospace',
                        fontWeight: '600',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        '&:active': {
                          backgroundColor: '#23827D'
                        }
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: isDarkMode ? '#F8FAFC' : '#0F172A',
                        fontFamily: 'JetBrains Mono, monospace',
                        fontWeight: '600'
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? '#1E293B' : '#FFFFFF',
                        border: `1px solid ${isDarkMode ? '#334155' : '#E2E8F0'}`,
                        borderRadius: '0.5rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        zIndex: 50
                      }),
                      menuList: (base) => ({
                        ...base,
                        padding: '4px'
                      })
                    }}
                    placeholder={t('selectLeverage')}
                  />
                  
                  {/* Account Requirements Info */}
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg border border-border">
                    <div className="flex items-start gap-2">
                      <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{t('minimumDeposit')}:</span>
                          <span className="font-bold text-foreground">
                            {createAccountData?.accountTypeData?.minDeposit === 0 
                              ? t('free') 
                              : `$${createAccountData?.accountTypeData?.minDeposit}`}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{t('maximumLeverage')}:</span>
                          <span className="font-bold text-primary">
                            {createAccountData?.accountTypeData?.maxLeverage}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                    <Icon name="AlertTriangle" size={12} />
                    {t('leverageWarning')}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateAccountModal(false)}
                  className="flex-1 h-11"
                >
                  <Icon name="X" size={16} className="mr-2" />
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={!createAccountData?.accountType || (createAccountData?.accountType === 'real' && !createAccountData?.accountSubType)}
                  className="flex-1 h-11 bg-gradient-to-r from-[#23827D] to-[#1a6862] hover:from-[#1a6862] hover:to-[#145a56] text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="Check" size={16} className="mr-2" />
                  {t('createAccount')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Notifications Panel */}
      <NotificationsPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        onMarkAllRead={markAllNotificationsRead}
        onMarkRead={markNotificationRead}
      />
      {/* Click outside handlers */}
      {(showAccountDropdown || showLanguageDropdown || showMobileAccountSelector || showUserMenu) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowAccountDropdown(false);
            setShowLanguageDropdown(false);
            setShowMobileAccountSelector(false);
            setShowUserMenu(false);
          }}
        />
      )}
    </>
  );
};

export default Header;